import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { formatApiError } from '../services/api';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Buildings, UserPlus, Warning } from '@phosphor-icons/react';

const RegisterPage = () => {
  const navigate = useNavigate();
  const { register } = useAuth();
  const [formData, setFormData] = useState({ name: '', email: '', password: '', confirmPassword: '' });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (formData.password !== formData.confirmPassword) {
      setError('Le password non corrispondono');
      return;
    }
    if (formData.password.length < 6) {
      setError('La password deve avere almeno 6 caratteri');
      return;
    }
    setLoading(true);
    try {
      await register(formData.email, formData.password, formData.name);
      navigate('/');
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      <div
        className="hidden lg:flex lg:w-1/2 bg-cover bg-center"
        style={{
          backgroundImage: 'url(https://images.unsplash.com/photo-1610741804272-059e1d3c5dba?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1OTN8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBvZmZpY2UlMjBhYnN0cmFjdCUyMGFyY2hpdGVjdHVyYWx8ZW58MHx8fHwxNzc1OTk4MTE2fDA&ixlib=rb-4.1.0&q=85)',
          backgroundColor: '#0A0A0A',
        }}
      >
        <div className="flex flex-col justify-end p-12 bg-gradient-to-t from-black/80 to-transparent w-full">
          <h1 className="font-heading text-4xl font-black text-white tracking-tighter mb-4">
            DCO Planner
          </h1>
          <p className="text-white/70 text-lg max-w-md">
            Unisciti alla tua organizzazione e gestisci i tuoi turni in modo efficiente.
          </p>
        </div>
      </div>

      <div className="flex-1 flex items-center justify-center p-8 bg-white">
        <div className="w-full max-w-md">
          <div className="flex items-center gap-3 mb-12">
            <div className="flex flex-col items-start">
              <img src="/logo-s2e.png" alt="DCO Planner" className="h-8 w-auto object-contain" />
              <div className="flex justify-between w-full mt-0.5">
                {'DCO PLANNER'.split('').map((ch, i) => (
                  <span key={i} className="font-heading text-[9px] font-bold text-[#0A0A0A]">{ch === ' ' ? '\u00A0' : ch}</span>
                ))}
              </div>
            </div>
          </div>

          <h2 className="font-heading text-3xl font-bold tracking-tight mb-2">Crea account</h2>
          <p className="text-[#52525B] mb-8">Registrati per unirti al tuo team</p>

          {error && (
            <div className="bg-[#FF2A00]/5 border border-[#FF2A00] p-4 mb-6 flex items-start gap-3" data-testid="register-error">
              <Warning className="text-[#FF2A00] flex-shrink-0 mt-0.5" size={20} weight="bold" />
              <p className="text-[#FF2A00] text-sm">{error}</p>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <Label htmlFor="name" className="text-xs tracking-[0.2em] uppercase font-bold text-[#52525B] mb-2 block">
                Nome Completo
              </Label>
              <Input id="name" name="name" type="text" value={formData.name} onChange={handleChange}
                placeholder="Mario Rossi"
                className="border border-[#E4E4E7] bg-white px-4 py-3 text-sm focus:border-[#0A0A0A] focus:ring-1 focus:ring-[#0A0A0A] rounded-none"
                data-testid="register-name-input" required />
            </div>
            <div>
              <Label htmlFor="email" className="text-xs tracking-[0.2em] uppercase font-bold text-[#52525B] mb-2 block">
                Indirizzo Email
              </Label>
              <Input id="email" name="email" type="email" value={formData.email} onChange={handleChange}
                placeholder="nome@azienda.com"
                className="border border-[#E4E4E7] bg-white px-4 py-3 text-sm focus:border-[#0A0A0A] focus:ring-1 focus:ring-[#0A0A0A] rounded-none"
                data-testid="register-email-input" required />
            </div>
            <div>
              <Label htmlFor="password" className="text-xs tracking-[0.2em] uppercase font-bold text-[#52525B] mb-2 block">
                Password
              </Label>
              <Input id="password" name="password" type="password" value={formData.password} onChange={handleChange}
                placeholder="Minimo 6 caratteri"
                className="border border-[#E4E4E7] bg-white px-4 py-3 text-sm focus:border-[#0A0A0A] focus:ring-1 focus:ring-[#0A0A0A] rounded-none"
                data-testid="register-password-input" required />
            </div>
            <div>
              <Label htmlFor="confirmPassword" className="text-xs tracking-[0.2em] uppercase font-bold text-[#52525B] mb-2 block">
                Conferma Password
              </Label>
              <Input id="confirmPassword" name="confirmPassword" type="password" value={formData.confirmPassword} onChange={handleChange}
                placeholder="Reinserisci la password"
                className="border border-[#E4E4E7] bg-white px-4 py-3 text-sm focus:border-[#0A0A0A] focus:ring-1 focus:ring-[#0A0A0A] rounded-none"
                data-testid="register-confirm-password-input" required />
            </div>
            <Button type="submit" disabled={loading}
              className="w-full bg-[#002FA7] text-white px-6 py-3 font-medium hover:bg-[#00278A] focus:ring-2 focus:ring-[#002FA7]/30 transition-colors rounded-none mt-2"
              data-testid="register-submit-btn">
              {loading ? (
                <span className="flex items-center justify-center gap-2">
                  <span className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full"></span>
                  Creazione in corso...
                </span>
              ) : (
                <span className="flex items-center justify-center gap-2">
                  <UserPlus size={20} weight="bold" />
                  Crea Account
                </span>
              )}
            </Button>
          </form>

          <p className="text-center text-sm text-[#52525B] mt-8">
            Hai già un account?{' '}
            <Link to="/login" className="text-[#002FA7] font-semibold hover:underline" data-testid="login-link">
              Accedi
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default RegisterPage;
